import { z } from "zod";
import {
  CashMovementTypeSchema,
  SafekeepingSettlementKindSchema,
} from "../entities/accounting.js";

const BusinessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: "businessDate must be YYYY-MM-DD",
});

export const OpenDayRequestSchema = z.object({
  businessDate: BusinessDateSchema,
  openingFloatCents: z.number().int().nonnegative(),
});
export type OpenDayRequest = z.infer<typeof OpenDayRequestSchema>;

export const CloseDayRequestSchema = z.object({
  countedCashCents: z.number().int().nonnegative(),
  notes: z.string().max(1000).nullable().default(null),
});
export type CloseDayRequest = z.infer<typeof CloseDayRequestSchema>;

export const ReopenDayRequestSchema = z.object({
  reason: z.string().min(3).max(500),
});
export type ReopenDayRequest = z.infer<typeof ReopenDayRequestSchema>;

export const CreateMovementRequestSchema = z
  .object({
    type: CashMovementTypeSchema,
    amountCents: z.number().int().positive(),
    categoryId: z.string().uuid().nullable().default(null),
    partnerId: z.string().uuid().nullable().default(null),
    heldByUserId: z.string().min(1).max(64).nullable().default(null),
    note: z.string().max(500).nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.type === "safekeeping") {
      if (!v.heldByUserId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["heldByUserId"],
          message: "heldByUserId is required for safekeeping",
        });
      }
      if (v.categoryId || v.partnerId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["type"],
          message: "safekeeping takes no category and no partner",
        });
      }
    } else if (v.heldByUserId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["heldByUserId"],
        message: "heldByUserId is only for safekeeping",
      });
    }
    if (v.type === "expense" && !v.categoryId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["categoryId"],
        message: "categoryId is required for an expense",
      });
    }
    if (v.type === "investor_payout" && !v.partnerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["partnerId"],
        message: "partnerId is required for an investor payout",
      });
    }
  });
export type CreateMovementRequest = z.infer<typeof CreateMovementRequestSchema>;

export const CreatePartnerRequestSchema = z.object({
  name: z.string().min(1).max(128),
  sharePercent: z.number().min(0).max(100).nullable().default(null),
});
export type CreatePartnerRequest = z.infer<typeof CreatePartnerRequestSchema>;

export const UpdatePartnerRequestSchema = z.object({
  name: z.string().min(1).max(128).optional(),
  sharePercent: z.number().min(0).max(100).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdatePartnerRequest = z.infer<typeof UpdatePartnerRequestSchema>;

export const CreateCategoryRequestSchema = z.object({
  name: z.string().min(1).max(64),
});
export type CreateCategoryRequest = z.infer<typeof CreateCategoryRequestSchema>;

export const UpdateCategoryRequestSchema = z.object({
  name: z.string().min(1).max(64).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateCategoryRequest = z.infer<typeof UpdateCategoryRequestSchema>;

export const DateRangeQuerySchema = z.object({
  from: BusinessDateSchema,
  to: BusinessDateSchema,
});
export type DateRangeQuery = z.infer<typeof DateRangeQuerySchema>;

export const SafekeepingSplitSchema = z
  .object({
    kind: SafekeepingSettlementKindSchema,
    amountCents: z.number().int().positive(),
    partnerId: z.string().uuid().nullable().default(null),
    categoryId: z.string().uuid().nullable().default(null),
    note: z.string().max(500).nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.kind === "payout" && !v.partnerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["partnerId"],
        message: "partnerId is required for a payout",
      });
    }
    if (v.kind === "expense" && !v.categoryId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["categoryId"],
        message: "categoryId is required for an expense",
      });
    }
    if (v.kind !== "payout" && v.partnerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["partnerId"],
        message: "partnerId is only for a payout",
      });
    }
    if (v.kind !== "expense" && v.categoryId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["categoryId"],
        message: "categoryId is only for an expense",
      });
    }
  });
export type SafekeepingSplit = z.infer<typeof SafekeepingSplitSchema>;
export type SafekeepingSplitInput = z.input<typeof SafekeepingSplitSchema>;

export const SettleSafekeepingRequestSchema = z.object({
  splits: z.array(SafekeepingSplitSchema).min(1).max(20),
});
export type SettleSafekeepingRequest = z.infer<typeof SettleSafekeepingRequestSchema>;
